import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import type RouterService from '@ember/routing/router-service';
import Icon from '../components/icon';
import MobileHeader from '../components/mobile-header.gts';
import ItemCard from '../components/item-card.gts';
import SearchIcon from '~icons/lucide/search';
import type SearchRoute from '../routes/search.ts';

interface SearchSignature {
  Args: {
    model: Awaited<ReturnType<SearchRoute['model']>>;
  };
}

export default class SearchTemplate extends Component<SearchSignature> {
  @service declare router: RouterService;
  @tracked searchQuery = '';

  @action
  updateQuery(event: Event) {
    this.searchQuery = (event.target as HTMLInputElement).value;
  }

  @action
  submitSearch(event: Event) {
    event.preventDefault();
    if (this.searchQuery.trim()) {
      void this.router.transitionTo('search', {
        queryParams: { q: this.searchQuery.trim(), page: '1' },
      });
    }
  }

  <template>
    <MobileHeader @title="Search" />
    <h1 class="mb-6 hidden text-3xl font-bold lg:block">Search</h1>

    <form class="mb-8 flex gap-2" {{on "submit" this.submitSearch}}>
      <input
        type="text"
        aria-label="Search query"
        placeholder="Search for movies, shows, books…"
        class="input input-bordered w-full"
        value={{this.searchQuery}}
        {{on "input" this.updateQuery}}
      />
      <button type="submit" class="btn btn-primary gap-2">
        <Icon @icon={{SearchIcon}} class="h-5 w-5" />
        Search
      </button>
    </form>

    {{#if @model.data.length}}
      <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {{#each @model.data as |item|}}
          <ItemCard @item={{item}} />
        {{/each}}
      </div>

      {{#if @model.meta}}
        <div class="mt-4 text-center">
          <span class="text-base-content/60 text-sm">
            {{@model.meta.total}}
            results found
          </span>
        </div>
      {{/if}}
    {{else if @model.meta}}
      <div class="text-base-content/60 py-12 text-center">
        No results found. Try a different search term.
      </div>
    {{else}}
      <div class="text-base-content/60 py-12 text-center">
        Enter a search term to find items.
      </div>
    {{/if}}
  </template>
}
